// ==================== RESELLERS PAGE ====================
// Super admin: reseller logins, their coupons and their commission payouts.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, RefreshCw, UsersRound } from 'lucide-react';
import { Badge, Button, Card, ConfirmDialog, Dropdown, Input, Pagination } from '../../components/ui';
import { resellersService, type ExistingCustomers, type Reseller } from '../../services/resellers.service';
import { ResellerFormModal } from '../../components/resellers/ResellerFormModal';
import { ResellerCouponsModal } from '../../components/resellers/ResellerCouponsModal';
import { ResellerCommissionsModal } from '../../components/resellers/ResellerCommissionsModal';
import { DeactivateResellerDialog } from '../../components/resellers/DeactivateResellerDialog';
import { CommissionAmount } from '../../components/resellers/CommissionAmount';

export const ResellersPage = () => {
  const [rows, setRows] = useState<Reseller[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<{ mode: 'create' | 'edit' | 'password'; reseller?: Reseller } | null>(null);
  const [coupons, setCoupons] = useState<Reseller | null>(null);
  const [commissions, setCommissions] = useState<Reseller | null>(null);
  const [deactivating, setDeactivating] = useState<Reseller | null>(null);
  const [deleting, setDeleting] = useState<Reseller | null>(null);
  // Search runs per keystroke: only the latest request may update the list
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    const response = await resellersService.getAll(page, limit, search);
    if (request !== latestRequest.current) return;
    if (response.success && response.data) {
      setRows(response.data.items);
      setMeta(response.data.meta);
      setError(null);
    } else {
      setError(response.message);
    }
    setLoading(false);
  }, [page, limit, search]);

  useEffect(() => {
    void Promise.resolve().then(load);
  }, [load]);

  const activate = async (reseller: Reseller) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    const response = await resellersService.update(reseller.id, { isActive: true });
    setSaving(false);
    if (response.success) {
      setNotice(`${reseller.firstName} ${reseller.lastName} reactivated and can sign in again.`);
      await load();
    } else {
      setError(response.message);
    }
  };

  const deactivate = async (existingCustomers: ExistingCustomers) => {
    if (!deactivating) return;
    setSaving(true);
    setError(null);
    const response = await resellersService.update(deactivating.id, { isActive: false, existingCustomers });
    setSaving(false);
    if (response.success) {
      const name = `${deactivating.firstName} ${deactivating.lastName}`;
      const affected = response.data?.affectedCustomers ?? 0;
      setNotice(
        existingCustomers === 'REMOVE'
          ? `${name} deactivated. ${affected} customer${affected === 1 ? '' : 's'} lost the coupon.`
          : `${name} deactivated. Existing customers keep their coupon.`,
      );
      setDeactivating(null);
      await load();
    } else {
      setError(response.message);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    setError(null);
    const response = await resellersService.delete(deleting.id);
    setSaving(false);
    if (response.success) {
      setNotice(`${deleting.firstName} ${deleting.lastName} deleted.`);
      setDeleting(null);
      await load();
    } else {
      setError(response.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white flex gap-3 items-center">
            <UsersRound className="text-(--color-signal-orange)" />
            Resellers
          </h1>
          <p className="text-sm text-(--color-muted-gray) mt-1">
            Manage reseller logins, coupons and commission payments.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => void load()} leftIcon={<RefreshCw size={16} />}>
            Refresh
          </Button>
          <Button
            onClick={() => {
              setNotice(null);
              setForm({ mode: 'create' });
            }}
            leftIcon={<Plus size={16} />}
          >
            Create Reseller
          </Button>
        </div>
      </div>

      {error && !deactivating && !deleting && (
        <p role="alert" className="text-sm text-(--color-error)">
          {error}
        </p>
      )}
      {notice && <p className="text-sm text-(--color-success)">{notice}</p>}

      <Card>
        <div className="mb-4 max-w-md">
          <Input
            label="Search resellers"
            placeholder="Name, email or company"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-(--color-muted-gray) border-b border-(--color-border-gray)">
              <tr>
                {[
                  'Name',
                  'Company',
                  'Email',
                  'Phone',
                  'Status',
                  'Coupons',
                  'Customers',
                  'Earned',
                  'Pending',
                  'Paid',
                  '',
                ].map((text) => (
                  <th key={text} className="p-3 font-medium whitespace-nowrap">
                    {text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-border-gray)">
              {rows.map((row) => (
                <tr key={row.id} className="text-(--color-soft-gray)">
                  <td className="p-3 text-white font-medium whitespace-nowrap">
                    {row.firstName} {row.lastName}
                  </td>
                  <td className="p-3">{row.companyName || '—'}</td>
                  <td className="p-3">{row.email}</td>
                  <td className="p-3 whitespace-nowrap">{row.phoneNumber || '—'}</td>
                  <td className="p-3">
                    <Badge variant={row.isActive ? 'success' : 'default'}>{row.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="p-3">{row.couponCount}</td>
                  <td className="p-3 whitespace-nowrap">
                    {row.customerCount}
                    <span className="block text-xs text-(--color-muted-gray)">{row.currentCustomerCount} current</span>
                  </td>
                  {(['earned', 'pending', 'paid'] as const).map((field) => (
                    <td key={field} className="p-3 whitespace-nowrap">
                      <CommissionAmount totals={row.totals} field={field} />
                    </td>
                  ))}
                  <td className="p-3">
                    <Dropdown
                      disabled={saving}
                      items={[
                        { key: 'coupons', label: 'Coupons', onClick: () => setCoupons(row) },
                        { key: 'commissions', label: 'Commissions', onClick: () => setCommissions(row) },
                        { key: 'edit', label: 'Edit', onClick: () => setForm({ mode: 'edit', reseller: row }) },
                        {
                          key: 'password',
                          label: 'Reset Password',
                          onClick: () => setForm({ mode: 'password', reseller: row }),
                        },
                        {
                          key: 'active',
                          label: row.isActive ? 'Deactivate' : 'Activate',
                          onClick: () => {
                            setError(null);
                            setNotice(null);
                            if (row.isActive) setDeactivating(row);
                            else void activate(row);
                          },
                        },
                        {
                          key: 'delete',
                          label: 'Delete',
                          colorClass: 'text-(--color-error)',
                          // Used resellers can only be deactivated
                          disabled: row.customerCount > 0,
                          onClick: () => {
                            setDeleting(row);
                            setError(null);
                            setNotice(null);
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-(--color-muted-gray)">
                    {loading
                      ? 'Loading resellers...'
                      : search
                        ? 'No resellers match your search.'
                        : 'No resellers yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={page}
          totalPages={meta.totalPages}
          totalItems={meta.total}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
          itemLabel="resellers"
          isLoading={loading}
        />
      </Card>

      {form && (
        <ResellerFormModal
          key={`${form.mode}-${form.reseller?.id ?? 'new'}`}
          mode={form.mode}
          reseller={form.reseller}
          onClose={() => setForm(null)}
          onSaved={() => {
            setNotice(
              form.mode === 'password'
                ? 'Password updated. Share it with the reseller.'
                : form.mode === 'edit'
                  ? 'Reseller updated.'
                  : 'Reseller created. Share the email and password with them; no email is sent.',
            );
            setForm(null);
            void load();
          }}
        />
      )}
      {coupons && (
        <ResellerCouponsModal reseller={coupons} onClose={() => setCoupons(null)} onUpdated={() => void load()} />
      )}
      {commissions && (
        <ResellerCommissionsModal
          reseller={commissions}
          onClose={() => setCommissions(null)}
          onUpdated={() => void load()}
        />
      )}
      {deactivating && (
        <DeactivateResellerDialog
          title={`Deactivate ${deactivating.firstName} ${deactivating.lastName}?`}
          effect="The reseller can no longer sign in and new customers can't use its coupons."
          currentCustomers={deactivating.currentCustomerCount}
          isLoading={saving}
          error={error}
          onClose={() => !saving && setDeactivating(null)}
          onConfirm={deactivate}
        />
      )}
      <ConfirmDialog
        isOpen={!!deleting}
        title="Delete Reseller?"
        description={
          <div>
            <p>
              Delete {deleting?.firstName} {deleting?.lastName} and its unused coupons? This cannot be undone.
            </p>
            {error && <p className="text-(--color-error)">{error}</p>}
          </div>
        }
        variant="danger"
        confirmText="Delete"
        isLoading={saving}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      />
    </div>
  );
};
